import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, LifeBuoy } from "lucide-react";
import api, { formatErrorDetail } from "@/lib/api";
import { PageHeader, TableWrap, Empty } from "@/components/app/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// One row per thing this employee has open with HR. Tickets are real records;
// leave, overtime, document requests and time corrections are projected from
// the requests themselves, so there is never a second copy to fall out of step.
const KIND = {
  ticket:              { label: "Ticket",          cls: "bg-slate-100 text-slate-700 border-slate-200" },
  leave:               { label: "Leave",           cls: "bg-blue-50 text-blue-700 border-blue-200" },
  overtime:            { label: "Overtime",        cls: "bg-violet-50 text-violet-700 border-violet-200" },
  document_request:    { label: "Document",        cls: "bg-amber-50 text-amber-700 border-amber-200" },
  time_adjustment:     { label: "Time correction", cls: "bg-teal-50 text-teal-700 border-teal-200" },
  schedule_adjustment: { label: "Shift change",    cls: "bg-teal-50 text-teal-700 border-teal-200" },
};

const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "-");

export default function MyTickets() {
  const [data, setData] = useState({ items: [], open_count: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "hr", priority: "normal", description: "" });

  const load = async () => {
    setLoading(true);
    try { const res = await api.get("/me/requests"); setData(res.data); }
    catch { setData({ items: [], open_count: 0, total: 0 }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const raise = async () => {
    if (!form.subject.trim()) return toast.error("Give your request a subject so HR knows what it is about.");
    try {
      await api.post("/me/tickets", form);
      toast.success("Sent to HR");
      setOpen(false);
      setForm({ subject: "", category: "hr", priority: "normal", description: "" });
      load();
    } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
  };

  const rows = data.items.filter((r) => showClosed || r.state === "open");

  return (
    <div>
      <PageHeader
        title="Help and Requests"
        subtitle="Raise something with HR and track everything you already have open."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className="h-11 sm:h-9 px-3 rounded-md bg-primary text-white text-sm flex items-center gap-2" data-testid="raise-ticket-btn">
                <Plus className="size-4" /> Raise a request
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Raise a request with HR</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Subject</Label>
                  <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                         placeholder="Correction on my August payslip" data-testid="ticket-subject" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category</Label>
                    <select className="w-full h-10 px-3 rounded-md border border-border" value={form.category}
                            onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="ticket-category">
                      <option value="hr">HR</option>
                      <option value="payroll">Payroll</option>
                      <option value="benefits">Benefits</option>
                    </select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <select className="w-full h-10 px-3 rounded-md border border-border" value={form.priority}
                            onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="ticket-priority">
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label>Details</Label>
                  <textarea className="w-full min-h-[110px] p-3 rounded-md border border-border text-sm"
                            value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="What do you need? Include dates or amounts if it helps."
                            data-testid="ticket-description" />
                </div>
                <div className="text-xs text-muted-foreground">
                  For leave, overtime, a certificate or a time correction, use the matching
                  page instead. Those follow their own approval route and appear in this
                  list automatically.
                </div>
              </div>
              <DialogFooter>
                <button className="h-11 sm:h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={raise} data-testid="ticket-submit">Send to HR</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex items-center justify-between mb-2 text-sm">
        <div className="text-muted-foreground">
          <span className="font-semibold text-foreground">{data.open_count}</span> open
          {data.total > data.open_count && <span> and {data.total - data.open_count} closed</span>}
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} data-testid="show-closed" />
          Show closed
        </label>
      </div>

      <TableWrap>
        <thead><tr><th>Type</th><th>Subject</th><th>Status</th><th>Raised</th><th></th></tr></thead>
        <tbody>
          {loading && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Loading...</td></tr>}
          {!loading && !rows.length && (
            <tr><td colSpan={5}>
              <Empty title={showClosed ? "Nothing yet" : "Nothing open"}
                     subtitle="Anything you raise with HR, and any leave, overtime or document request, shows up here." />
            </td></tr>
          )}
          {!loading && rows.map((r) => {
            const k = KIND[r.kind] || KIND.ticket;
            return (
              <tr key={r.kind + "-" + r.id} data-testid={"req-" + r.kind}>
                <td><span className={"rounded-full border px-2 py-0.5 text-xs font-semibold " + k.cls}>{k.label}</span></td>
                <td className="font-medium">{r.subject}</td>
                <td><span className={"badge " + (r.state === "open" ? "badge-info" : "badge-approved")}>{r.status}</span></td>
                <td className="text-muted-foreground">{fmt(r.created_at)}</td>
                <td className="text-right">
                  {r.kind !== "ticket" && <a className="text-xs text-primary" href={r.link}>Open</a>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </TableWrap>

      <div className="mt-3 text-xs text-muted-foreground flex items-start gap-2">
        <LifeBuoy className="size-3.5 mt-0.5" />
        <span>
          Leave, overtime, document and time-correction rows appear here so you can see
          everything in one place. They are tracked by their own approval route, and this
          list reflects that route rather than duplicating it.
        </span>
      </div>
    </div>
  );
}
